/**
 * PaymentRails · Federation standard payment block.
 *
 * The single canonical set of four payment rails, in the Architect-mandated
 * order: Gumroad → PayPal → JIM → Crypto. Placed wherever payments appear
 * (Footer, Redeem page, Backer page).
 *
 * Rendered as pre-rendered semantic HTML (real <a> hrefs + visible crypto
 * addresses) so crawlers and the react-snap SSG pass see the block without
 * executing JavaScript. The copy buttons are progressive enhancement only.
 */
import React, { useState } from "react";

export const PAYMENT_RAILS = {
  gumroad: "https://keywebster.gumroad.com/l/olhsf",
  paypal: "https://paypal.me/nextxus/5.90",
  jim: "https://jim.com/a/roger-christopher-keyser",
  btc: "bc1qzwjzf83u7nz2fx4frchncvgvyu902xfwn2y8dm",
  eth: "0xdC436a568AcC70D2c34B07f2590dd94f0F2Fd702",
};

const GOLD = "#ffd24a";
const GOLD_DEEP = "#ffb200";
const CYAN = "#00f0ff";

function RailCard({ children, testid, accent = GOLD_DEEP }) {
  return (
    <div
      data-testid={testid}
      style={{
        background: "rgba(6,8,20,0.72)",
        border: `1px solid ${accent}55`,
        borderRadius: 10,
        padding: "18px 18px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {children}
    </div>
  );
}

function RailHead({ n, name, accent }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
      <span
        className="font-display"
        style={{ color: accent, fontSize: 16, letterSpacing: "0.04em", textShadow: `0 0 10px ${accent}55` }}
      >
        {name}
      </span>
      <span
        className="font-mono"
        style={{
          fontSize: 9,
          letterSpacing: "0.22em",
          color: accent,
          border: `1px solid ${accent}66`,
          borderRadius: 3,
          padding: "2px 7px",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        Rail {n}
      </span>
    </div>
  );
}

const linkBtnStyle = (accent) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  minHeight: 40,
  padding: "0 16px",
  marginTop: "auto",
  background: `linear-gradient(135deg, ${GOLD_DEEP} 0%, ${GOLD} 100%)`,
  color: "#0a0700",
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  textDecoration: "none",
  border: `1px solid ${accent}`,
  borderRadius: 2,
});

const addrRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

export function PaymentRails({
  heading = "Support the Federation",
  subtitle = "Four ways to pay · $5 = 1 Access Token",
  className = "",
  style = {},
}) {
  const [copied, setCopied] = useState("");

  const copy = (key, val) => {
    try {
      navigator.clipboard?.writeText(val);
      setCopied(key);
      setTimeout(() => setCopied(""), 1600);
    } catch (e) {
      /* clipboard blocked in some sandboxes — addresses are visible as text */
    }
  };

  return (
    <section
      data-testid="payment-rails"
      aria-label="Payment options"
      className={className}
      style={{
        background: "rgba(3,4,12,0.6)",
        border: "1px solid rgba(255,178,0,0.28)",
        borderRadius: 12,
        padding: "22px 20px",
        ...style,
      }}
    >
      <header style={{ marginBottom: 16 }}>
        <h3
          className="font-display"
          style={{ color: GOLD, fontSize: 20, letterSpacing: "0.03em", margin: 0, textShadow: `0 0 12px ${GOLD}44` }}
        >
          {heading}
        </h3>
        <p
          className="font-mono"
          style={{ color: "rgba(230,251,255,0.6)", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", margin: "6px 0 0" }}
        >
          {subtitle}
        </p>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14,
        }}
      >
        {/* ── RAIL 1 · Gumroad ─────────────────────────────────── */}
        <RailCard testid="payment-rail-gumroad" accent={GOLD_DEEP}>
          <RailHead n={1} name="Gumroad" accent={GOLD_DEEP} />
          <p className="font-mono" style={{ color: "rgba(230,251,255,0.65)", fontSize: 11, lineHeight: 1.5, margin: 0 }}>
            Card / Apple Pay · get a license key emailed instantly, then redeem it for tokens.
          </p>
          <a
            href={PAYMENT_RAILS.gumroad}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="payment-rail-gumroad-link"
            style={linkBtnStyle(GOLD_DEEP)}
          >
            Buy on Gumroad →
          </a>
        </RailCard>

        {/* ── RAIL 2 · PayPal ──────────────────────────────────── */}
        <RailCard testid="payment-rail-paypal" accent="#00a0ff">
          <RailHead n={2} name="PayPal" accent="#4aa8ff" />
          <p className="font-mono" style={{ color: "rgba(230,251,255,0.65)", fontSize: 11, lineHeight: 1.5, margin: 0 }}>
            Send $5.90 directly through PayPal — covers one token plus processing.
          </p>
          <a
            href={PAYMENT_RAILS.paypal}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="payment-rail-paypal-link"
            style={{ ...linkBtnStyle("#4aa8ff"), background: "linear-gradient(135deg,#0070ba 0%,#00a0ff 100%)", color: "#f2fbff" }}
          >
            Pay with PayPal →
          </a>
        </RailCard>

        {/* ── RAIL 3 · JIM ─────────────────────────────────────── */}
        <RailCard testid="payment-rail-jim" accent="#a756ff">
          <RailHead n={3} name="JIM" accent="#c98bff" />
          <p className="font-mono" style={{ color: "rgba(230,251,255,0.65)", fontSize: 11, lineHeight: 1.5, margin: 0 }}>
            Support the Architect directly through the JIM tip rail.
          </p>
          <a
            href={PAYMENT_RAILS.jim}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="payment-rail-jim-link"
            style={{ ...linkBtnStyle("#c98bff"), background: "linear-gradient(135deg,#7b2ff7 0%,#a756ff 100%)", color: "#f7f2ff" }}
          >
            Pay with JIM →
          </a>
        </RailCard>

        {/* ── RAIL 4 · Crypto ──────────────────────────────────── */}
        <RailCard testid="payment-rail-crypto" accent="#00f0ff">
          <RailHead n={4} name="Crypto" accent={CYAN} />
          <p className="font-mono" style={{ color: "rgba(230,251,255,0.65)", fontSize: 11, lineHeight: 1.5, margin: 0 }}>
            Send $5 in BTC or ETH, then post the tx hash on the board to mint.
          </p>

          <div style={{ marginTop: 4 }}>
            <div className="font-mono" style={{ fontSize: 9, letterSpacing: "0.3em", color: "rgba(230,251,255,0.5)", textTransform: "uppercase", marginBottom: 3 }}>
              BTC · Bitcoin
            </div>
            <div style={addrRowStyle}>
              <code
                data-testid="payment-rail-btc-address"
                style={{ flex: 1, minWidth: 0, fontFamily: "ui-monospace,Menlo,monospace", fontSize: 10, color: GOLD, wordBreak: "break-all", userSelect: "all" }}
              >
                {PAYMENT_RAILS.btc}
              </code>
              <button
                type="button"
                onClick={() => copy("btc", PAYMENT_RAILS.btc)}
                data-testid="payment-rail-btc-copy"
                aria-label="Copy BTC address"
                style={{ background: "none", border: `1px solid ${GOLD}66`, color: GOLD, borderRadius: 3, fontSize: 9, letterSpacing: "0.18em", padding: "3px 7px", cursor: "pointer", whiteSpace: "nowrap" }}
              >
                {copied === "btc" ? "✓" : "COPY"}
              </button>
            </div>
          </div>

          <div style={{ marginTop: 6 }}>
            <div className="font-mono" style={{ fontSize: 9, letterSpacing: "0.3em", color: "rgba(230,251,255,0.5)", textTransform: "uppercase", marginBottom: 3 }}>
              ETH · Ethereum
            </div>
            <div style={addrRowStyle}>
              <code
                data-testid="payment-rail-eth-address"
                style={{ flex: 1, minWidth: 0, fontFamily: "ui-monospace,Menlo,monospace", fontSize: 10, color: CYAN, wordBreak: "break-all", userSelect: "all" }}
              >
                {PAYMENT_RAILS.eth}
              </code>
              <button
                type="button"
                onClick={() => copy("eth", PAYMENT_RAILS.eth)}
                data-testid="payment-rail-eth-copy"
                aria-label="Copy ETH address"
                style={{ background: "none", border: `1px solid ${CYAN}66`, color: CYAN, borderRadius: 3, fontSize: 9, letterSpacing: "0.18em", padding: "3px 7px", cursor: "pointer", whiteSpace: "nowrap" }}
              >
                {copied === "eth" ? "✓" : "COPY"}
              </button>
            </div>
          </div>
        </RailCard>
      </div>
    </section>
  );
}

export default PaymentRails;
